/**
 * QUALITY-PHASE2 — External Client Complaints / External Errors (Admin / TL only)
 * Log, review and resolve client-reported quality errors. Records feed the
 * employee "Errors This Week" metric and the order Activity Timeline.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, Loader2, CheckCircle2, XCircle, PlayCircle, BadgeCheck, Paperclip, Eye, Download, Upload, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const CATEGORIES = [
  { value: 'data_error', label: 'Data Error' },
  { value: 'missing_info', label: 'Missing Info' },
  { value: 'document_issue', label: 'Document Issue' },
  { value: 'calculation_issue', label: 'Calculation Issue' },
  { value: 'wrong_jurisdiction', label: 'Wrong Jurisdiction' },
];
const SEVERITIES = ['minor', 'major', 'critical'];

const STATUS_STYLES = {
  open: 'bg-amber-100 text-amber-800',
  under_review: 'bg-blue-100 text-blue-800',
  verified: 'bg-purple-100 text-purple-800',
  dismissed: 'bg-gray-200 text-gray-600',
  resolved: 'bg-emerald-100 text-emerald-800',
};
const SEVERITY_STYLES = { minor: 'bg-gray-100 text-gray-700', major: 'bg-orange-100 text-orange-800', critical: 'bg-red-100 text-red-800' };

const inputCls = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelCls = "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";

const emptyForm = { order_number: '', processor_id: '', category: '', severity: '', error_type: '', description: '' };
const emptyFilters = { status: '', category: '', severity: '', processor_id: '', reported_by: '', date_from: '', date_to: '' };

export const ExternalErrorsTab = () => {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [orderInfo, setOrderInfo] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [formProcessors, setFormProcessors] = useState(null); // team-scoped list for the create form
  const [reporters, setReporters] = useState([]);
  const [detail, setDetail] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [uploadingSection, setUploadingSection] = useState(null);
  const clientFileRef = useRef(null);
  const processorFileRef = useRef(null);

  const loadIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API}/quality/incidents${qs}`, { headers: authHeaders() });
      const data = await res.json();
      setIncidents(data.incidents || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load external errors'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { loadIncidents(); }, [loadIncidents]);
  useEffect(() => {
    fetch(`${API}/quality/processors`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setProcessors(d.processors || [])).catch(() => {});
    fetch(`${API}/quality/reporters`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setReporters(d.reporters || [])).catch(() => {});
  }, []);

  const lookupOrder = async () => {
    if (!form.order_number.trim()) return;
    setLookupBusy(true);
    setOrderInfo(null);
    setFormProcessors(null);
    try {
      const res = await fetch(`${API}/quality/order-lookup?order_number=${encodeURIComponent(form.order_number.trim())}`, { headers: authHeaders() });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Order not found'); setLookupBusy(false); return; }
      const info = await res.json();
      setOrderInfo(info);
      // Context-aware processor list: tax order → tax processors, search order → search processors
      let teamList = null;
      if (info.team) {
        try {
          const pr = await fetch(`${API}/quality/processors?team=${info.team}`, { headers: authHeaders() });
          const pd = await pr.json();
          teamList = pd.processors || [];
          setFormProcessors(teamList);
        } catch { setFormProcessors(null); }
      }
      // Pre-select the suggested processor only if they're a valid choice for this team
      if (info.suggested_processor_id && (!teamList || teamList.some(p => p.id === info.suggested_processor_id))) {
        setForm(f => ({ ...f, processor_id: info.suggested_processor_id }));
      } else {
        setForm(f => ({ ...f, processor_id: '' }));
      }
      toast.success(`Order ${info.order_number} found`);
    } catch { toast.error('Lookup failed'); }
    setLookupBusy(false);
  };

  const submit = async () => {
    if (!orderInfo) { toast.error('Look up the order first'); return; }
    if (!form.processor_id || !form.category || !form.severity || !form.description.trim()) {
      toast.error('Processor, Category, Severity and Description are required'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/quality/incidents`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Failed to create'); setSaving(false); return; }
      toast.success('External error logged');
      setShowForm(false); setForm(emptyForm); setOrderInfo(null);
      loadIncidents();
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const transition = async (incident, status) => {
    setActionBusy(true);
    try {
      const body = { status };
      if (status === 'resolved') body.resolution_notes = resolutionNotes;
      const res = await fetch(`${API}/quality/incidents/${incident.id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Update failed'); setActionBusy(false); return; }
      const data = await res.json();
      setDetail(data.incident);
      setResolutionNotes('');
      toast.success(`Marked ${status.replace('_', ' ')}`);
      loadIncidents();
    } catch { toast.error('Update failed'); }
    setActionBusy(false);
  };

  const uploadAttachment = async (section, fileObj) => {
    if (!detail || !fileObj) return;
    if (fileObj.size > 2 * 1024 * 1024) { toast.error('File too large — maximum 2 MB per file'); return; }
    setUploadingSection(section);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      const res = await fetch(`${API}/quality/incidents/${detail.id}/attachments?section=${section}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Upload failed'); setUploadingSection(null); return; }
      const data = await res.json();
      setDetail(data.incident);
      toast.success(`${fileObj.name} uploaded`);
      loadIncidents();
    } catch { toast.error('Upload failed'); }
    setUploadingSection(null);
  };

  const openAttachment = async (fileEntry, mode) => {
    try {
      const res = await fetch(`${API}/quality/incidents/${detail.id}/attachments/${fileEntry.file_id}/download?inline=${mode === 'view'}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) { toast.error('Could not fetch file'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'view') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = fileEntry.filename; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not fetch file'); }
  };

  const AttachmentSection = ({ title, section, files, fileRef }) => (
    <div className="border border-gray-200 rounded-lg p-3" data-testid={`attachments-${section}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          <Paperclip className="w-3.5 h-3.5" /> {title} <span className="text-gray-400 normal-case">({(files || []).length})</span>
        </div>
        <input type="file" ref={fileRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadAttachment(section, f); }} />
        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={uploadingSection === section}
          onClick={() => fileRef.current?.click()} data-testid={`upload-${section}-btn`}>
          {uploadingSection === section ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          Upload
        </Button>
      </div>
      {(files || []).length === 0 ? (
        <div className="text-xs text-gray-400 py-1">No files uploaded. PDF, JPG, PNG, DOCX, XLSX — max 2 MB each.</div>
      ) : (
        <div className="space-y-1.5">
          {files.map(f => (
            <div key={f.file_id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-md px-2.5 py-1.5" data-testid={`attachment-row-${f.file_id}`}>
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-800 truncate">{f.filename}</div>
                <div className="text-[11px] text-gray-500">{f.uploaded_by_name} · {formatDateTimeLocal(f.uploaded_at)}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openAttachment(f, 'view')} className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800" title="View" data-testid={`view-attachment-${f.file_id}`}>
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => openAttachment(f, 'download')} className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800" title="Download" data-testid={`download-attachment-${f.file_id}`}>
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="external-errors-tab">
      {/* Filter bar */}
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap" data-testid="external-errors-filter-bar">
        <div className="flex items-end gap-2 flex-wrap">
          {[
            { key: 'status', label: 'Status', options: Object.keys(STATUS_STYLES).map(s => ({ value: s, label: s.replace('_', ' ') })) },
            { key: 'category', label: 'Category', options: CATEGORIES },
            { key: 'severity', label: 'Severity', options: SEVERITIES.map(s => ({ value: s, label: s })) },
            { key: 'processor_id', label: 'Processor', options: processors.map(p => ({ value: p.id, label: p.full_name })) },
            { key: 'reported_by', label: 'Reported By', options: reporters.map(p => ({ value: p.id, label: p.full_name })) },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{f.label}</label>
              <select className={`${inputCls} w-36 py-1.5`} value={filters[f.key]}
                onChange={e => setFilters(prev => ({ ...prev, [f.key]: e.target.value }))}
                data-testid={`external-errors-${f.key.replace('_', '-')}-filter`}>
                <option value="">All</option>
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Reported From</label>
            <input type="date" className={`${inputCls} w-36 py-1.5`} value={filters.date_from}
              onChange={e => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              data-testid="external-errors-date-from-filter" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Reported To</label>
            <input type="date" className={`${inputCls} w-36 py-1.5`} value={filters.date_to}
              onChange={e => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              data-testid="external-errors-date-to-filter" />
          </div>
          {Object.values(filters).some(Boolean) && (
            <button onClick={() => setFilters(emptyFilters)}
              className="flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
              data-testid="external-errors-reset-filters-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
            </button>
          )}
          <span className="text-xs text-gray-500 pb-2.5">{total} record{total === 1 ? '' : 's'}</span>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 ml-auto" data-testid="log-external-error-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Log External Error
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : incidents.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500" data-testid="external-errors-empty">No external errors recorded{Object.values(filters).some(Boolean) ? ' matching the current filters' : ''}.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="tf-thead text-xs uppercase">
              <tr>
                {['Order #', 'Processor', 'Category', 'Severity', 'Status', 'Reported', 'Reported By'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidents.map(inc => (
                <tr key={inc.id} className="hover:bg-blue-50/40 cursor-pointer" onClick={() => setDetail(inc)} data-testid={`external-error-row-${inc.order_number}`}>
                  <td className="px-4 py-2.5 font-medium text-blue-700">{inc.order_number}</td>
                  <td className="px-4 py-2.5">{inc.processor_name}</td>
                  <td className="px-4 py-2.5">{(CATEGORIES.find(c => c.value === inc.category) || {}).label || inc.category}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SEVERITY_STYLES[inc.severity] || ''}`}>{inc.severity}</span></td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[inc.status] || ''}`}>{inc.status.replace('_', ' ')}</span></td>
                  <td className="px-4 py-2.5 text-gray-500">{inc.reported_at ? formatDateOnly(inc.reported_at) : '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500">{inc.reported_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setForm(emptyForm); setOrderInfo(null); setFormProcessors(null); } }}>
        <DialogContent className="max-w-lg" data-testid="log-external-error-dialog">
          <DialogHeader><DialogTitle>Log External Client Error</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Order Number *</label>
              <div className="flex gap-2">
                <input className={inputCls} value={form.order_number} onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))}
                  placeholder="TF-20260626-44692" data-testid="external-error-order-input" />
                <Button variant="outline" onClick={lookupOrder} disabled={lookupBusy} data-testid="external-error-lookup-btn">
                  {lookupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {orderInfo && (
                <div className="mt-2 text-xs bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-blue-900" data-testid="external-error-order-info">
                  <span className="font-semibold">{orderInfo.order_number}</span> · {orderInfo.client_code} · {orderInfo.client_product || '—'} · {orderInfo.state || '—'}
                  {orderInfo.team && <> · <span className="font-semibold uppercase">{orderInfo.team} team</span></>}
                  {orderInfo.suggested_processor_name && <> · Processor: <span className="font-semibold">{orderInfo.suggested_processor_name}</span></>}
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Responsible Processor *{orderInfo?.team && <span className="text-blue-500 normal-case"> ({orderInfo.team} team only)</span>}</label>
              <select className={inputCls} value={form.processor_id} onChange={e => setForm(f => ({ ...f, processor_id: e.target.value }))} data-testid="external-error-processor-select">
                <option value="">Select processor...</option>
                {(formProcessors ?? processors).map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Category *</label>
                <select className={inputCls} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="external-error-category-select">
                  <option value="">Select...</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Severity *</label>
                <select className={inputCls} value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} data-testid="external-error-severity-select">
                  <option value="">Select...</option>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Error Type (optional)</label>
              <input className={inputCls} value={form.error_type} onChange={e => setForm(f => ({ ...f, error_type: e.target.value }))}
                placeholder="e.g. Wrong tax amount reported" data-testid="external-error-type-input" />
            </div>
            <div>
              <label className={labelCls}>Complaint Description *</label>
              <textarea className={`${inputCls} min-h-[90px]`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What did the client report?" data-testid="external-error-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="external-error-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Log Error
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / review dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setResolutionNotes(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="external-error-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.order_number}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[detail.status] || ''}`}>{detail.status.replace('_', ' ')}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2.5 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div><span className="text-gray-500">Processor:</span> <span className="font-medium">{detail.processor_name}</span></div>
                  <div><span className="text-gray-500">Client:</span> {detail.client_code}</div>
                  <div><span className="text-gray-500">Category:</span> {(CATEGORIES.find(c => c.value === detail.category) || {}).label || detail.category}</div>
                  <div><span className="text-gray-500">Severity:</span> <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${SEVERITY_STYLES[detail.severity] || ''}`}>{detail.severity}</span></div>
                  <div><span className="text-gray-500">Reported:</span> {detail.reported_at ? formatDateTimeLocal(detail.reported_at) : '—'}</div>
                  <div><span className="text-gray-500">By:</span> {detail.reported_by_name}</div>
                </div>
                {detail.error_type && <div><span className="text-gray-500">Error type:</span> {detail.error_type}</div>}
                <div className="bg-gray-50 rounded-lg p-3 text-gray-700 whitespace-pre-wrap">{detail.description}</div>
                {/* QUALITY-PHASE2b — attachment audit trail */}
                <AttachmentSection title="Supporting Documents (Client Evidence)" section="client_evidence"
                  files={detail.client_evidence_files} fileRef={clientFileRef} />
                <AttachmentSection title="Processor Response Documents" section="processor_response"
                  files={detail.processor_response_files} fileRef={processorFileRef} />
                {(detail.processor_comments || []).length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3" data-testid="processor-comments-section">
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Processor Comments</div>
                    <div className="space-y-1.5">
                      {detail.processor_comments.map(c => (
                        <div key={c.id} className="bg-gray-50 rounded-md px-2.5 py-1.5">
                          <div className="text-xs text-gray-700 whitespace-pre-wrap">{c.comment}</div>
                          <div className="text-[11px] text-gray-400 mt-0.5">{c.by_name} · {formatDateTimeLocal(c.at)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.reviewed_by_name && <div className="text-xs text-gray-500">Reviewed by {detail.reviewed_by_name} · {detail.reviewed_at ? formatDateTimeLocal(detail.reviewed_at) : ''}</div>}
                {detail.resolution_notes && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <div className="text-xs font-semibold text-emerald-700 mb-1">Resolution — {detail.resolved_by_name}</div>
                    <div className="text-emerald-900 whitespace-pre-wrap">{detail.resolution_notes}</div>
                  </div>
                )}
                {detail.status === 'verified' && (
                  <div>
                    <label className={labelCls}>Resolution Notes * (required to resolve)</label>
                    <textarea className={`${inputCls} min-h-[70px]`} value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} data-testid="external-error-resolution-input" />
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {detail.status === 'open' && (
                  <>
                    <Button variant="outline" onClick={() => transition(detail, 'dismissed')} disabled={actionBusy} data-testid="external-error-dismiss-btn">
                      <XCircle className="w-4 h-4 mr-1.5" /> Dismiss
                    </Button>
                    <Button onClick={() => transition(detail, 'under_review')} disabled={actionBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="external-error-review-btn">
                      <PlayCircle className="w-4 h-4 mr-1.5" /> Start Review
                    </Button>
                  </>
                )}
                {detail.status === 'under_review' && (
                  <>
                    <Button variant="outline" onClick={() => transition(detail, 'dismissed')} disabled={actionBusy} data-testid="external-error-dismiss-btn">
                      <XCircle className="w-4 h-4 mr-1.5" /> Dismiss
                    </Button>
                    <Button onClick={() => transition(detail, 'verified')} disabled={actionBusy} className="bg-purple-600 hover:bg-purple-700" data-testid="external-error-verify-btn">
                      <BadgeCheck className="w-4 h-4 mr-1.5" /> Verify
                    </Button>
                  </>
                )}
                {detail.status === 'verified' && (
                  <Button onClick={() => transition(detail, 'resolved')} disabled={actionBusy || !resolutionNotes.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="external-error-resolve-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Resolve
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalErrorsTab;
